/**
 * Conversation export utilities
 * Export conversation history to JSON file
 */

import logger from './logger';
import { TIMELINE_SYSTEM_EVENT_TYPES } from '../constants/messages';
import { GEMATIK_ROOM } from '../constants';

/**
 * Parse geo URI (RFC 5870) to extract latitude and longitude
 * Format: geo:lat,lon[,alt][;params]
 * @param {string} geoUri - The geo URI to parse
 * @returns {{latitude: number|null, longitude: number|null}}
 */
function parseGeoUri(geoUri) {
  if (!geoUri || typeof geoUri !== 'string') {
    return { latitude: null, longitude: null };
  }

  const match = geoUri.match(/^geo:([-+]?\d+\.?\d*),([-+]?\d+\.?\d*)/);
  if (!match) {
    return { latitude: null, longitude: null };
  }

  const lat = parseFloat(match[1]);
  const lon = parseFloat(match[2]);

  // Validate ranges
  if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    return { latitude: null, longitude: null };
  }

  return { latitude: lat, longitude: lon };
}

/**
 * Process reactions from timeline events and group by target message
 * @param {Array} events - Timeline events
 * @param {Object} room - Matrix room object
 * @returns {Map<string, Map<string, Array>>} Map of eventId -> emoji -> reactors
 */
function processReactionsForExport(events, room) {
  const reactionsByMessage = new Map();

  events.forEach(event => {
    if (event.getType() !== 'm.reaction') return;

    const content = event.getContent();
    const relatesTo = content['m.relates_to'];
    if (!relatesTo || relatesTo.rel_type !== 'm.annotation') return;

    const targetEventId = relatesTo.event_id;
    const emoji = relatesTo.key;
    const senderId = event.getSender();
    const member = room?.getMember(senderId);
    const displayName = member?.name || senderId;

    if (!reactionsByMessage.has(targetEventId)) {
      reactionsByMessage.set(targetEventId, new Map());
    }
    const messageReactions = reactionsByMessage.get(targetEventId);
    if (!messageReactions.has(emoji)) {
      messageReactions.set(emoji, []);
    }
    messageReactions.get(emoji).push({ senderId, displayName });
  });

  return reactionsByMessage;
}

/**
 * Convert reactions Map to plain object for JSON export
 * @param {Map<string, Array>} reactionsMap - Map of emoji -> reactors
 * @returns {Object|null} Plain object or null if no reactions
 */
function reactionsMapToObject(reactionsMap) {
  if (!reactionsMap || reactionsMap.size === 0) {
    return null;
  }
  const result = {};
  reactionsMap.forEach((reactors, emoji) => {
    result[emoji] = reactors;
  });
  return result;
}

/**
 * Format a system event for export with placeholder text matching on-screen display
 * @param {Object} event - Matrix event
 * @param {Object} room - Matrix room object
 * @returns {Object|null} Formatted system event or null if should be filtered
 */
function formatSystemEventForExport(event, room) {
  const sender = event.getSender();
  const timestamp = event.getTs();
  const eventType = event.getType();
  const content = event.getContent() || {};
  const prevContent = event.getPrevContent?.() || {};
  const eventId = event.getId();
  const senderMember = room?.getMember(sender);
  const senderName = senderMember?.name || sender;

  const baseData = {
    eventId,
    sender,
    timestamp: new Date(timestamp).toISOString(),
    type: eventType,
    messageType: 'system',
  };

  switch (eventType) {
    case 'm.room.create':
      return {
        ...baseData,
        systemEventType: 'room_created',
        placeholder: `Conversation created on ${new Date(timestamp).toLocaleDateString()}`,
      };

    // Gematik custom events - filter out (duplicates of standard events)
    case GEMATIK_ROOM.ROOM_NAME:
    case GEMATIK_ROOM.ROOM_TOPIC:
      return null;

    case 'm.room.name': {
      const newName = content.name;
      const prevName = prevContent.name;
      return {
        ...baseData,
        systemEventType: 'room_name_changed',
        placeholder: newName
          ? `${senderName} changed the room name ${prevName ? `from ${prevName} ` : ''}to ${newName}`
          : `${senderName} removed the room name`,
        details: { newName, previousName: prevName },
      };
    }

    case 'm.room.topic': {
      const newTopic = content.topic;
      const prevTopic = prevContent.topic;
      return {
        ...baseData,
        systemEventType: 'room_topic_changed',
        placeholder: newTopic
          ? `${senderName} changed the room topic ${prevTopic ? `from ${prevTopic} ` : ''}to ${newTopic}`
          : `${senderName} removed the room topic`,
        details: { newTopic, previousTopic: prevTopic },
      };
    }

    case 'm.room.avatar': {
      const prevUrl = prevContent.url;
      const newUrl = content.url;
      let action;
      if (!prevUrl && newUrl) action = 'set';
      else if (prevUrl && newUrl) action = 'changed';
      else action = 'removed';
      return {
        ...baseData,
        systemEventType: 'room_avatar_changed',
        placeholder: `${senderName} ${action} the room's avatar`,
        details: { action, url: newUrl },
      };
    }

    case 'm.room.member': {
      const membership = content.membership;
      const prevMembership = prevContent.membership;
      const targetUserId = event.getStateKey?.() || sender;
      const targetMember = room?.getMember(targetUserId);
      const targetName = content.displayname || targetMember?.name || targetUserId;
      const avatarChanged =
        membership === 'join' &&
        prevMembership === 'join' &&
        content.avatar_url !== prevContent.avatar_url &&
        (content.avatar_url || prevContent.avatar_url);

      if (avatarChanged) {
        return {
          ...baseData,
          systemEventType: 'profile_changed',
          placeholder: `${targetName} changed their profile picture`,
          details: { displayName: targetName, targetUserId, avatarChanged: true },
        };
      } else if (membership === 'join' && prevMembership !== 'join') {
        return {
          ...baseData,
          systemEventType: 'member_joined',
          placeholder: `${targetName} joined the room`,
          details: { membership, displayName: targetName, targetUserId },
        };
      } else if (membership === 'leave' && prevMembership === 'join') {
        const isKick = sender !== targetUserId;
        if (isKick) {
          return {
            ...baseData,
            systemEventType: 'member_kicked',
            placeholder: `${senderName} removed ${targetName} from the room`,
            details: { membership, displayName: targetName, targetUserId, kickedBy: sender },
          };
        } else {
          return {
            ...baseData,
            systemEventType: 'member_left',
            placeholder: `${targetName} left the room`,
            details: { membership, displayName: targetName, targetUserId },
          };
        }
      } else if (membership === 'leave' && prevMembership === 'invite') {
        const userDeclined = sender === targetUserId;
        if (userDeclined) {
          return {
            ...baseData,
            systemEventType: 'invite_declined',
            placeholder: `${targetName} declined the invitation`,
            details: { membership, displayName: targetName, targetUserId },
          };
        } else {
          return {
            ...baseData,
            systemEventType: 'invite_rescinded',
            placeholder: `${senderName} rescinded ${targetName}'s invitation`,
            details: { membership, displayName: targetName, targetUserId, rescindedBy: sender },
          };
        }
      } else if (membership === 'invite') {
        // Filter out re-invite messages (invite after leave)
        if (prevMembership === 'leave') {
          return null;
        }
        return {
          ...baseData,
          systemEventType: 'member_invited',
          placeholder: `${senderName} invited ${targetName}`,
          details: { membership, displayName: targetName, targetUserId, invitedBy: sender },
        };
      } else if (membership === 'ban') {
        return {
          ...baseData,
          systemEventType: 'member_banned',
          placeholder: `${targetName} was banned`,
          details: { membership, displayName: targetName, targetUserId, bannedBy: sender },
        };
      } else {
        // Unhandled membership transition
        return null;
      }
    }

    case 'm.room.join_rules': {
      const rule = content.join_rule;
      const ruleText = {
        public: 'made this room public',
        invite: 'made this room invite-only',
        knock: 'changed this room to allow knocking',
        restricted: 'made this room restricted',
      }[rule] || 'changed the room access rules';
      return {
        ...baseData,
        systemEventType: 'join_rules_changed',
        placeholder: `${senderName} ${ruleText}`,
        details: { joinRule: rule },
      };
    }

    case 'm.room.history_visibility': {
      const visibility = content.history_visibility;
      const visibilityText = {
        world_readable: 'made future room history visible to anyone',
        shared: 'made future room history visible to all room members',
        invited: 'made future room history visible to all room members from the point they are invited',
        joined: 'made future room history visible to all room members from the point they joined',
      }[visibility] || 'changed room history visibility';
      return {
        ...baseData,
        systemEventType: 'history_visibility_changed',
        placeholder: `${senderName} ${visibilityText}`,
        details: { historyVisibility: visibility },
      };
    }

    case 'm.room.power_levels': {
      const users = content.users || {};
      const prevUsers = prevContent.users || {};

      // Find users whose power levels changed
      const changedUsers = [];
      Object.keys({ ...users, ...prevUsers }).forEach(userId => {
        const newLevel = users[userId];
        const oldLevel = prevUsers[userId];
        if (newLevel !== oldLevel && newLevel !== undefined) {
          const member = room?.getMember(userId);
          const userDisplayName = member?.name || userId.split(':')[0].substring(1);
          changedUsers.push({ userId, oldLevel: oldLevel || 0, newLevel, displayName: userDisplayName });
        }
      });

      let placeholder;
      if (changedUsers.length === 1) {
        const change = changedUsers[0];
        if (change.newLevel === 100) {
          placeholder = `${senderName} made ${change.displayName} an admin`;
        } else if (change.newLevel === 50) {
          placeholder = `${senderName} made ${change.displayName} a moderator`;
        } else if (change.newLevel === 0 && change.oldLevel > 0) {
          placeholder = `${senderName} revoked ${change.displayName}'s moderator status`;
        } else {
          placeholder = `${senderName} changed ${change.displayName}'s power level from ${change.oldLevel} to ${change.newLevel}`;
        }
      } else if (changedUsers.length > 1) {
        placeholder = `${senderName} changed the power levels of ${changedUsers.length} users`;
      } else {
        placeholder = `${senderName} changed the room permissions`;
      }

      return {
        ...baseData,
        systemEventType: 'power_levels_changed',
        placeholder,
        details: { changedUsers },
      };
    }

    case 'm.room.pinned_events': {
      const pinnedEvents = content.pinned || [];
      const prevPinnedEvents = prevContent.pinned || [];

      let action;
      if (pinnedEvents.length === 0 && prevPinnedEvents.length > 0) {
        action = 'unpinned all messages';
      } else if (pinnedEvents.length > prevPinnedEvents.length) {
        action = 'pinned a message';
      } else if (pinnedEvents.length < prevPinnedEvents.length) {
        action = 'unpinned a message';
      } else {
        action = 'changed pinned messages';
      }

      return {
        ...baseData,
        systemEventType: 'pinned_events_changed',
        placeholder: `${senderName} ${action}`,
        details: { pinnedCount: pinnedEvents.length, previousPinnedCount: prevPinnedEvents.length },
      };
    }

    case 'm.room.tombstone': {
      const replacementRoom = content.replacement_room;
      return {
        ...baseData,
        systemEventType: 'room_upgraded',
        placeholder: replacementRoom
          ? `${senderName} upgraded this room. The conversation continues in a new room.`
          : `${senderName} closed this room`,
        details: { replacementRoom },
      };
    }

    case 'm.room.encryption': {
      return {
        ...baseData,
        systemEventType: 'encryption_enabled',
        placeholder: `${senderName} enabled encryption`,
        details: { algorithm: content.algorithm },
      };
    }

    // Silent events - don't export
    case 'm.room.guest_access':
    case 'm.room.canonical_alias':
    case 'm.room.third_party_invite':
      return null;

    default:
      logger.debug(`[conversationExport] Unhandled system event type: ${eventType}`);
      return null;
  }
}

/**
 * Format a Matrix event for export
 * @param {Object} event - Matrix event
 * @param {Object} room - Matrix room object (for member lookups)
 * @param {Map} reactionsMap - Optional reactions for this event
 * @returns {Object} Formatted event data
 */
function formatEventForExport(event, room, reactionsMap = null) {
  const sender = event.getSender();
  const timestamp = event.getTs();
  const eventType = event.getType();
  const content = event.getContent();
  const eventId = event.getId();

  const baseData = {
    eventId,
    sender,
    timestamp: new Date(timestamp).toISOString(),
    type: eventType,
  };

  // Handle redacted (deleted) messages
  if (event.isRedacted?.()) {
    const redactionInfo = event.getUnsigned()?.redacted_because;
    const deletedBy = redactionInfo?.sender || 'unknown';
    const deletedAt = redactionInfo?.origin_server_ts
      ? new Date(redactionInfo.origin_server_ts).toISOString()
      : null;
    const deletionReason = redactionInfo?.content?.reason || null;

    // Get display name for who deleted the message
    let deletedByName = deletedBy;
    if (room && deletedBy !== 'unknown') {
      const member = room.getMember(deletedBy);
      if (member?.name) {
        deletedByName = member.name;
      }
    }

    return {
      ...baseData,
      messageType: 'deleted',
      isRedacted: true,
      deletedBy,
      deletedByName,
      deletedAt,
      ...(deletionReason && { deletionReason }),
    };
  }

  // Add reactions if present
  const reactions = reactionsMapToObject(reactionsMap);

  // Build the type-specific portion of the event
  let typeSpecific;

  if (eventType === 'm.room.message') {
    const msgType = content.msgtype;
    const editedBody = content.body;
    const originalContent = event.getOriginalContent?.();
    const originalBody = (originalContent && originalContent.body !== undefined && originalContent.body !== editedBody)
      ? originalContent.body
      : null;
    const body = originalBody ?? editedBody;

    const relatesTo = content['m.relates_to'];
    const editOf = relatesTo?.rel_type === 'm.replace' ? relatesTo.event_id : null;

    switch (msgType) {
      case 'm.text':
        typeSpecific = { messageType: 'text', body };
        break;
      case 'm.image': {
        // For encrypted media, URL is in content.file.url; for unencrypted, in content.url
        const mediaUrl = content.file?.url || content.url;
        const thumbnailFile = content.info?.thumbnail_file;
        const thumbnailUrl = thumbnailFile?.url || content.info?.thumbnail_url;
        typeSpecific = {
          messageType: 'image',
          body,
          attachment: {
            type: 'image',
            filename: content.filename || body,
            mimeType: content.info?.mimetype,
            size: content.info?.size,
            url: mediaUrl,
            ...(thumbnailUrl && { thumbnailUrl }),
            ...(content.file && { encrypted: true }),
            ...(thumbnailFile && { thumbnailEncrypted: true }),
          },
        };
        break;
      }
      case 'm.file': {
        const mediaUrl = content.file?.url || content.url;
        typeSpecific = {
          messageType: 'file',
          body,
          attachment: {
            type: 'file',
            filename: content.filename || body,
            mimeType: content.info?.mimetype,
            size: content.info?.size,
            url: mediaUrl,
            ...(content.file && { encrypted: true }),
          },
        };
        break;
      }
      case 'm.video': {
        const mediaUrl = content.file?.url || content.url;
        const thumbnailFile = content.info?.thumbnail_file;
        const thumbnailUrl = thumbnailFile?.url || content.info?.thumbnail_url;
        typeSpecific = {
          messageType: 'video',
          body,
          attachment: {
            type: 'video',
            filename: content.filename || body,
            mimeType: content.info?.mimetype,
            size: content.info?.size,
            duration: content.info?.duration,
            url: mediaUrl,
            ...(thumbnailUrl && { thumbnailUrl }),
            ...(content.file && { encrypted: true }),
            ...(thumbnailFile && { thumbnailEncrypted: true }),
          },
        };
        break;
      }
      case 'm.audio': {
        const mediaUrl = content.file?.url || content.url;
        typeSpecific = {
          messageType: 'audio',
          body,
          attachment: {
            type: 'audio',
            filename: content.filename || body,
            mimeType: content.info?.mimetype,
            size: content.info?.size,
            duration: content.info?.duration,
            url: mediaUrl,
            ...(content.file && { encrypted: true }),
          },
        };
        break;
      }
      case 'm.location': {
        const geoUri = content.geo_uri || '';
        const { latitude, longitude } = parseGeoUri(geoUri);
        typeSpecific = {
          messageType: 'location',
          body,
          location: {
            geoUri,
            latitude,
            longitude,
            displayText: body || 'Location',
          },
        };
        break;
      }
      default:
        typeSpecific = { messageType: msgType, body };
        break;
    }

    return {
      ...baseData,
      ...typeSpecific,
      ...(editOf && { editOf }),
      ...(reactions && { reactions }),
    };
  } else if (eventType === 'm.room.encrypted') {
    typeSpecific = { messageType: 'encrypted', body: '[Encrypted message]' };
  } else {
    typeSpecific = { messageType: 'other', content };
  }

  return {
    ...baseData,
    ...typeSpecific,
    ...(reactions && { reactions }),
  };
}

/**
 * Export conversation history to JSON file
 * @param {Object} client - Matrix client instance
 * @param {string} roomId - Room ID to export
 * @param {number} limit - Maximum number of messages to export (default: 500)
 * @returns {Promise<void>}
 */
export async function exportConversationHistory(client, roomId, limit = 500) {
  if (!client || !roomId) {
    throw new Error('Client and roomId are required');
  }

  try {
    logger.info('[exportConversationHistory] Starting export for room:', roomId);

    const room = client.getRoom(roomId);
    if (!room) {
      throw new Error('Room not found');
    }

    // Get room metadata
    const roomName = room.name || 'Unknown Room';
    const roomTopic = room.currentState.getStateEvents('m.room.topic', '')?.getContent()?.topic || '';

    // Check m.direct account data to determine if this is a DM room
    // A room is a DM only if it appears in the user's m.direct account data
    const directEvent = client.getAccountData('m.direct');
    const directContent = directEvent?.getContent() || {};
    const isDM = Object.values(directContent).some(
      (roomIds) => Array.isArray(roomIds) && roomIds.includes(roomId),
    );

    // Get timeline events
    const timeline = room.getLiveTimeline();
    const events = timeline.getEvents();

    // Paginate backwards to get more history if needed
    let allEvents = [...events];
    let canPaginate = true;

    while (allEvents.length < limit && canPaginate) {
      try {
        const result = await client.paginateEventTimeline(timeline, {
          backwards: true,
          limit: Math.min(100, limit - allEvents.length),
        });

        if (result) {
          const newEvents = timeline.getEvents();
          if (newEvents.length === allEvents.length) {
            // No new events, stop paginating
            canPaginate = false;
          } else {
            allEvents = newEvents;
          }
        } else {
          canPaginate = false;
        }
      } catch (paginateError) {
        logger.warn('[exportConversationHistory] Pagination failed:', paginateError);
        canPaginate = false;
      }
    }

    // Take only the requested number of most recent messages
    const recentEvents = allEvents.slice(-limit);

    // Process reactions first
    const reactionsByMessage = processReactionsForExport(recentEvents, room);

    // Filter and format events
    const formattedEvents = recentEvents
      .filter(event => {
        const type = event.getType();
        // Export message events, system events, but NOT reaction events (they're aggregated)
        return type === 'm.room.message' ||
          type === 'm.room.encrypted' ||
          TIMELINE_SYSTEM_EVENT_TYPES.includes(type);
      })
      .map(event => {
        const eventType = event.getType();
        const eventId = event.getId();
        const content = event.getContent();
        const relatesTo = content['m.relates_to'];
        const reactionsLookupId = (relatesTo?.rel_type === 'm.replace') ? relatesTo.event_id : eventId;
        const reactionsForEvent = reactionsByMessage.get(reactionsLookupId) || reactionsByMessage.get(eventId);

        // Handle system events separately
        if (TIMELINE_SYSTEM_EVENT_TYPES.includes(eventType) && eventType !== 'm.room.message') {
          return formatSystemEventForExport(event, room);
        }

        return formatEventForExport(event, room, reactionsForEvent);
      })
      .filter(event => event !== null); // Filter out null events (filtered system events)

    // Build a lookup by eventId so edits can check whether their target was deleted
    const formattedById = new Map(formattedEvents.map(e => [e.eventId, e]));

    // Drop edit events whose original message has been deleted
    const exportData = formattedEvents.filter(event => {
      if (!event.editOf) return true;
      const original = formattedById.get(event.editOf);
      return !original || original.messageType !== 'deleted';
    });

    // Create export object
    const exportObject = {
      exportDate: new Date().toISOString(),
      roomId,
      roomName,
      roomTopic,
      isDM,
      messageCount: exportData.length,
      messages: exportData,
    };

    // Convert to JSON
    const jsonString = JSON.stringify(exportObject, null, 2);

    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    try {
      const link = document.createElement('a');
      link.href = url;

      // Create filename with room name and date
      const sanitizedRoomName = roomName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `conversation_${sanitizedRoomName}_${dateStr}.json`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      // Always revoke URL to prevent memory leaks
      URL.revokeObjectURL(url);
    }

    logger.info('[exportConversationHistory] Export completed successfully:', {
      roomId,
      messageCount: exportData.length,
    });

  } catch (error) {
    logger.error('[exportConversationHistory] Export failed:', error);
    throw new Error(`Failed to export conversation: ${error.message}`);
  }
}

// Export helper functions for testing
export {
  parseGeoUri,
  processReactionsForExport,
  reactionsMapToObject,
  formatSystemEventForExport,
  formatEventForExport,
};
